import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadError } from '../../components/LoadError';
import { Dialog } from '../../components/Dialog';
import { ErrorSummary } from '../../components/FormFeedback';
import { MathText } from '../../components/MathText';
import { PageFrame } from '../../components/PageFrame';
import { ContentUnavailableState } from './ContentUnavailableState';
import { SafeExitLink } from './SafeExitLink';
import { OrderAnswer, TickAllAnswer } from './ListAnswers';
import { readListOptions, splitAnswer } from './listAnswer';
import { BoxesInSentence, LabelledBoxes } from './BoxesAnswer';
import {
  boxesInSentence,
  boxesOrder,
  joinBoxValues,
  pressBoxKey,
  readBoxes,
  splitBoxValues,
} from './boxesAnswerText';
import { FractionAnswer } from './FractionAnswer';
import {
  fractionOrder,
  isFractionPart,
  joinFractionParts,
  pressFractionKey,
  splitFractionParts,
} from './fractionAnswerText';
import { BuildGraphAnswer } from './BuildGraphAnswer';
import { BuildPictureGraphAnswer } from './BuildPictureGraphAnswer';
import { MakeAmountAnswer } from './MakeAmountAnswer';
import { MatchAnswer } from './MatchAnswer';
import { NumberPad } from './NumberPad';
import { PlaceAnswer } from './PlaceAnswer';
import { ReadAloudButton } from './ReadAloudButton';
import { canReadAloud, readAloudScript } from './readAloud';
import { SetClockAnswer } from './SetClockAnswer';
import { SortAnswer } from './SortAnswer';
import { readSort, sortedCount, sortOrder } from './sortAnswerText';
import {
  buildOrderFromTray,
  readBuild,
} from '../../platform/content/buildAnswer';
import { clockEntry, splitClockText } from '../../platform/content/clockAnswer';
import {
  buildOrder,
  builtGraph,
  readBuildGraph,
} from '../../platform/content/buildGraphAnswer';
import {
  filledGraph,
  fillOrder,
  readBuildPictureGraph,
} from '../../platform/content/buildPictureGraphAnswer';
import {
  readFractionAccept,
  readFractionNegatives,
} from '../../platform/content/fractionAnswer';
import {
  joinedCount,
  matchOrder,
  readMatch,
} from '../../platform/content/matchAnswer';
import {
  canonicalNumberLineValue,
  numberLineEntry,
  numberLineTick,
  readNumberLineContent,
} from '../../platform/content/numberLineAnswer';
import { PracticeDiagramView } from '../../platform/content/practice-diagram';
import {
  readPracticeDiagram,
  type PracticeDiagramSpec,
} from '../../platform/content/practice-diagram-data';
import { FractionModelView } from '../../platform/content/practice-fraction-model';
import { CoordinateGrid } from '../../platform/content/practice-graphs';
import { NumberLineView } from '../../platform/content/practice-number-line';
import { ShadeGridView } from '../../platform/content/practice-shade-grid';
import {
  gridPointOrder,
  joinGridPoints,
  shadedEntry,
  splitGridPoints,
  splitShaded,
  toggleGridPoint,
  toggleShaded,
} from '../../platform/content/tapAnswer';
import type {
  createLearnerSessionClient,
  CurrentPractice,
} from '../../platform/learner/learner-session-client';

// Same discipline as SessionIntroduction/StudentStart: one fixed,
// learner-safe message, never the raw error text or a server-specific
// reason (IDEMPOTENCY_CONFLICT, ACTION_NOT_ALLOWED, CAPABILITY_INVALID,
// INTERNAL_ERROR all collapse into this single calm, retryable message).
// The server only accepts numbers below this size (1e15, see
// dm_imp_005_validate_response_schema). A longer number used to be sent
// anyway and came back as the "check your connection" error, which was
// misleading (tester report 2026-10-03). 15 characters can never reach it.
const NUMERIC_LIMIT = 1e15;
const NUMERIC_MAX_LENGTH = 15;

const GENERIC_ACTION_ERROR =
  "That didn't go through — check your connection and try again.";
const PAUSE_NOT_SAVED_NOTE =
  'This might not have been saved — you can try again.';
const END_EARLY_NOT_SAVED_NOTE =
  'This might not have been saved — you can try again.';
// UX audit: "You can't undo this" reads like a delete-file warning --
// harsher than the moment calls for, for a 5-12 year old. Same honest
// meaning (there's no coming back to this exact activity today), warmer
// framing.
const END_EARLY_CONFIRM_TEXT =
  "Do you want to stop for now? You won't be able to come back to this activity today.";

// DM-IMP-005 changes-required remediation, Task 5 (finding 5, final
// resolution): terminal capabilities are now revoked atomically on the
// server the instant a transition happens, so a follow-up
// getCurrentPractice() call after a terminal action response would
// always fail with CAPABILITY_INVALID. record_session_action's own
// response already carries the authoritative resulting session_status
// and progress -- route directly from it instead of refetching.
const TERMINAL_ROUTES: Record<string, string> = {
  completed: '/learn/complete',
  ended_early: '/learn/ended',
  abandoned: '/learn/ended',
};

type Props = {
  client: Pick<
    ReturnType<typeof createLearnerSessionClient>,
    'getCurrentPractice' | 'sendAction'
  >;
};

function readPrompt(content: unknown): string | null {
  if (typeof content !== 'object' || content === null) return null;
  const record = content as Record<string, unknown>;
  return typeof record.prompt === 'string' ? record.prompt : null;
}

// Tap-to-choose questions (response form 'selection', 2026-09-28): the
// item's own options, read defensively. The server only accepts an option
// id that is in this same list.
//
// An option may be a picture (14 Oct 2026): it then carries a `diagram`, laid
// out like the question's own, and its label may be left out. An option
// whose picture cannot be read spoils the whole list, so a child is never
// shown a choice with a blank in it.
type ChoiceOption = {
  id: string;
  label: string;
  diagram: PracticeDiagramSpec | null;
};
function readOptions(content: unknown): ChoiceOption[] | null {
  if (typeof content !== 'object' || content === null) return null;
  const options = (content as Record<string, unknown>).options;
  if (!Array.isArray(options) || options.length === 0) return null;
  const valid: ChoiceOption[] = [];
  for (const option of options) {
    if (typeof option !== 'object' || option === null) return null;
    const record = option as Record<string, unknown>;
    if (typeof record.id !== 'string') return null;
    if (record.diagram === undefined) {
      if (typeof record.label !== 'string') return null;
      valid.push({ id: record.id, label: record.label, diagram: null });
      continue;
    }
    const diagram = readPracticeDiagram({ diagram: record.diagram });
    if (diagram === null) return null;
    if (record.label !== undefined && typeof record.label !== 'string')
      return null;
    valid.push({ id: record.id, label: record.label ?? '', diagram });
  }
  return valid;
}

// 'list': tick-all and put-in-order answers (ListAnswers.tsx), sent as
// the chosen ids in order. 'boxes': several typed-number boxes
// (BoxesAnswer.tsx), sent the same way as one "id=number" for each box.
// 'sort': items put into groups (SortAnswer.tsx), sent the same way as one
// "item=group" for each item. 'fraction': one typed fraction or mixed number
// (FractionAnswer.tsx), sent the same way as the one entry "f=<fraction>".
// 'point': one tick tapped on the number line in the picture
// (practice-number-line.tsx), sent the same way as the one entry "p=<value>".
// 'plot': points tapped on the grid in the picture, sent the same way as one
// "pt=x,y" for each. 'shade': parts shaded on the picture, sent the same way
// as the one entry "shaded=<count>" (tapAnswer.ts). 'build': missing columns
// drawn on a column graph (BuildGraphAnswer.tsx), sent the same way as one
// "bar=<column>.<set>=<height>" for each. 'clock': the hands of a clock set
// with buttons (SetClockAnswer.tsx), sent the same way as the one entry
// "time=<h>:<mm>" (clockAnswer.ts). 'make': coins and notes or blocks tapped
// into a tray (MakeAmountAnswer.tsx), sent the same way as one
// "<piece>=<count>" for each piece, or the one entry "total=<amount>"
// (buildAnswer.ts). 'match': each item joined to its partner
// (MatchAnswer.tsx), sent the same way as one "pair=<item>-<partner>" for
// each item (matchAnswer.ts). A "place the number" question (PlaceAnswer.tsx)
// is sent as 'point': it is a tapped point on the number line. 'rows':
// pictures added to rows of a picture graph (BuildPictureGraphAnswer.tsx),
// sent the same way as one "row=<row>=<count>" for each row
// (buildPictureGraphAnswer.ts).
type ResponseKind =
  | 'numeric'
  | 'text'
  | 'selection'
  | 'list'
  | 'boxes'
  | 'sort'
  | 'fraction'
  | 'point'
  | 'plot'
  | 'shade'
  | 'build'
  | 'clock'
  | 'make'
  | 'match'
  | 'rows';

// The picture a "tap a point on the grid" question is answered on, or null.
function readPlotGrid(content: unknown) {
  const diagram = readPracticeDiagram(content);
  return diagram?.type === 'coordinate_grid' ? diagram : null;
}

// The picture a "shade the parts" question is answered on, with how many
// parts it has, or null. A fraction model of several shapes is never one.
function readShadePicture(content: unknown) {
  const diagram = readPracticeDiagram(content);
  // A grid the student shades starts empty.
  if (diagram?.type === 'shade_grid' && diagram.shaded === 0)
    return { picture: diagram, parts: diagram.rows * diagram.columns };
  if (diagram?.type === 'fraction_model' && diagram.shaded.length === 1)
    return { picture: diagram, parts: diagram.parts };
  return null;
}

export function PracticeTask({ client }: Props) {
  const navigate = useNavigate();
  const [practice, setPractice] = useState<CurrentPractice | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [actionError, setActionError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [pauseFailed, setPauseFailed] = useState(false);
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [endingEarly, setEndingEarly] = useState(false);
  const [endEarlyFailed, setEndEarlyFailed] = useState(false);
  const [responseText, setResponseText] = useState('');
  // Several-boxes questions: the box the number pad types into (the one
  // last tapped or tabbed to). Null means the first box. Fraction entry
  // uses it the same way for its three boxes; null there means the top
  // number.
  const [activeBoxId, setActiveBoxId] = useState<string | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() =>
    crypto.randomUUID(),
  );
  // Brief, neutral acknowledgment after a submitted answer -- never
  // "Correct!"/"Wrong!": submit_response's real response never carries a
  // correctness verdict (evaluation happens server-side, separately from
  // this call), so claiming one here would be fabricated. This just
  // confirms the submission went through and progress moved on, closing
  // the gap where the previous flow silently jumped to the next question
  // with no acknowledgment at all.
  const [justSubmitted, setJustSubmitted] = useState(false);
  const justSubmittedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (justSubmittedTimer.current) clearTimeout(justSubmittedTimer.current);
    };
  }, []);

  // Guards against a stale in-flight Pause/End-Early success arriving after
  // the learner has already navigated away by a different route — clicking
  // Help or Report a problem while Pause is pending, for example. Neither
  // of those affordances calls sendAction (pure navigation), so they can't
  // be "in flight" themselves, but they do unmount this component, and
  // without this guard a late-resolving pause/end_early would still fire
  // its navigate(..., { replace: true }) against the router directly,
  // silently overwriting wherever the learner has since ended up. Same
  // class of bug as the earlier Help-state trap: an action whose success
  // path is reachable from a state the learner may no longer be in.
  const mountedRef = useRef(true);
  useEffect(() => {
    // Set on every mount, not only at creation: React's development
    // StrictMode mounts, unmounts and remounts, which otherwise left this
    // false and silently skipped the finish/pause/stop navigation.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Shows the next question. Used both when the server sends it back with
  // the submitted answer (no second trip, 2026-10-03) and after a fresh read.
  const showNext = useCallback((result: CurrentPractice) => {
    setPractice(result);
    setLoadError(false);
    setResponseText('');
    setActiveBoxId(null);
    // Ready straight away: the next question must never show a grey,
    // disabled-looking Submit while the last answer finishes (2026-10-01).
    setSubmitting(false);
    setIdempotencyKey(crypto.randomUUID());
  }, []);

  const load = useCallback(() => {
    return client
      .getCurrentPractice()
      .then((result) => {
        showNext(result);
        return result;
      })
      .catch(() => {
        setLoadError(true);
      });
  }, [client, showNext]);

  useEffect(() => {
    let active = true;
    setPractice(null);
    setLoadError(false);
    client
      .getCurrentPractice()
      .then((result) => {
        if (active) {
          setPractice(result);
          setIdempotencyKey(crypto.randomUUID());
        }
      })
      .catch(() => {
        if (active) setLoadError(true);
      });
    return () => {
      active = false;
    };
  }, [client]);

  // Terminal-state routing by sessionStatus, not just "currentItem is
  // null": a completed session goes to /learn/complete, every other
  // terminal status (ended_early, abandoned, or any future terminal value
  // not yet reachable by any code path) goes to the generic /learn/ended
  // fallback. This is a navigate, not a conditional render — once the
  // terminal status is known, PracticeTask hands off entirely rather than
  // duplicating what the destination screens render.
  useEffect(() => {
    if (practice === null) return;
    if (
      practice.contentEligibilityException !== null ||
      practice.currentItem !== null
    ) {
      return;
    }
    const destination =
      practice.sessionStatus === 'completed'
        ? '/learn/complete'
        : '/learn/ended';
    void navigate(destination, { replace: true });
  }, [practice, navigate]);

  function handleResponseChange(value: string) {
    setResponseText(value);
    // A new, unmodified attempt starts here — this is a new submission, so
    // it needs a fresh idempotency key. Retrying the *same* unmodified
    // input after a failure must NOT regenerate the key (it isn't touched
    // anywhere else), so a same-input retry replays under the original key.
    setIdempotencyKey(crypto.randomUUID());
  }

  async function handleSubmit(kind: ResponseKind) {
    const isNumeric = kind === 'numeric';
    const trimmed = responseText.trim();
    // A clock not touched yet still shows a time (12 o'clock), which may be
    // the answer.
    if (trimmed.length === 0 && kind !== 'clock') return;
    // Real backend contract (dm_imp_005_validate_response_schema): a
    // numeric item's response must be responseKind='numeric' with value
    // exactly {"answer": <real JSON number>} -- not the text_bounded
    // {"text": <string>} shape every other response form uses. Getting
    // this wrong doesn't fail loudly (a numeric answer typed as text
    // still passes text_bounded's own, looser string validation), it
    // just silently evaluates the response under the wrong kind.
    if (isNumeric) {
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || Math.abs(parsed) >= NUMERIC_LIMIT) return;
    }
    // Several boxes: nothing is sent until every box holds a number.
    const boxOrder =
      kind === 'boxes'
        ? boxesOrder(
            readBoxes(practice?.currentItem?.content) ?? [],
            responseText,
          )
        : null;
    if (kind === 'boxes' && boxOrder === null) return;
    // Sort into groups: nothing is sent until every item is in a group.
    const sortQuestion =
      kind === 'sort' ? readSort(practice?.currentItem?.content) : null;
    const sortEntries = sortQuestion
      ? sortOrder(sortQuestion, responseText)
      : null;
    if (kind === 'sort' && sortEntries === null) return;
    // Fraction entry: nothing is sent until the top and bottom numbers make
    // a fraction.
    const fractionEntries =
      kind === 'fraction'
        ? fractionOrder(
            responseText,
            readFractionAccept(practice?.currentItem?.content),
          )
        : null;
    if (kind === 'fraction' && fractionEntries === null) return;
    // Tap a point: nothing is sent until a tick of the line is chosen.
    const pointLine =
      kind === 'point'
        ? readNumberLineContent(practice?.currentItem?.content)
        : null;
    const pointAt = pointLine ? numberLineTick(pointLine, responseText) : null;
    const pointEntries =
      pointLine && pointAt !== null
        ? [numberLineEntry(canonicalNumberLineValue(pointLine, pointAt))]
        : null;
    if (kind === 'point' && pointEntries === null) return;
    // Tap points on the grid: nothing is sent until a point is chosen.
    const plotEntries =
      kind === 'plot' ? gridPointOrder(splitGridPoints(responseText)) : null;
    if (plotEntries?.length === 0) return;
    // Shade the parts: nothing is sent until a part is shaded.
    const shadeParts =
      kind === 'shade'
        ? (readShadePicture(practice?.currentItem?.content)?.parts ?? 0)
        : 0;
    const shadeCount =
      kind === 'shade' ? splitShaded(responseText, shadeParts).length : 0;
    if (kind === 'shade' && shadeCount === 0) return;
    // Draw the columns: nothing is sent until every column is drawn.
    const buildQuestion =
      kind === 'build' ? readBuildGraph(practice?.currentItem?.content) : null;
    const buildEntries = buildQuestion
      ? buildOrder(responseText, buildQuestion)
      : null;
    if (kind === 'build' && buildEntries === null) return;
    // Set the clock: the time the hands show.
    const clockAt =
      kind === 'clock' ? clockEntry(splitClockText(trimmed)) : null;
    // Make the amount: nothing is sent while the tray is empty.
    const makeQuestion =
      kind === 'make' ? readBuild(practice?.currentItem?.content) : null;
    const makeEntries = makeQuestion
      ? buildOrderFromTray(makeQuestion, responseText)
      : null;
    if (kind === 'make' && makeEntries === null) return;
    // Match: nothing is sent until every item is joined.
    const matchQuestion =
      kind === 'match' ? readMatch(practice?.currentItem?.content) : null;
    const matchEntries = matchQuestion
      ? matchOrder(matchQuestion, responseText)
      : null;
    if (kind === 'match' && matchEntries === null) return;
    // Add pictures: nothing is sent while a row to fill is empty.
    const rowsQuestion =
      kind === 'rows'
        ? readBuildPictureGraph(practice?.currentItem?.content)
        : null;
    const rowsEntries = rowsQuestion
      ? fillOrder(responseText, rowsQuestion)
      : null;
    if (kind === 'rows' && rowsEntries === null) return;
    const entries =
      boxOrder ??
      sortEntries ??
      fractionEntries ??
      pointEntries ??
      plotEntries ??
      buildEntries ??
      makeEntries ??
      matchEntries ??
      rowsEntries ??
      (clockAt !== null ? [clockAt] : null) ??
      (kind === 'shade' ? [shadedEntry(shadeCount)] : null);
    setSubmitting(true);
    setActionError(false);
    try {
      const result = await client.sendAction(
        'submit_response',
        kind === 'selection'
          ? { responseKind: 'selection', value: { optionId: trimmed } }
          : kind === 'list'
            ? {
                responseKind: 'ordered_set',
                value: { order: splitAnswer(responseText) },
              }
            : entries !== null
              ? {
                  responseKind: 'ordered_set',
                  value: { order: entries },
                }
              : isNumeric
                ? {
                    responseKind: 'numeric',
                    value: { answer: Number(trimmed) },
                  }
                : { responseKind: 'text_bounded', value: { text: trimmed } },
        idempotencyKey,
      );
      const terminalRoute = TERMINAL_ROUTES[result.sessionStatus];
      if (terminalRoute !== undefined) {
        if (mountedRef.current) void navigate(terminalRoute, { replace: true });
        return;
      }
      if (mountedRef.current) {
        setJustSubmitted(true);
        if (justSubmittedTimer.current)
          clearTimeout(justSubmittedTimer.current);
        justSubmittedTimer.current = setTimeout(() => {
          setJustSubmitted(false);
        }, 2000);
      }
      if (result.practice) {
        if (mountedRef.current) showNext(result.practice);
      } else await load();
    } catch {
      setActionError(true);
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePause() {
    setPausing(true);
    setPauseFailed(false);
    try {
      await client.sendAction('pause', {}, crypto.randomUUID());
      // Only navigate on confirmed success — never claim the session is
      // paused via a `finally` or before the server has actually
      // confirmed it, same discipline as every other action here. Also
      // skip navigation entirely if the learner has since navigated away
      // by another route (see mountedRef above) — this call already
      // succeeded server-side, so the session really is paused, but
      // forcing a stale navigation on top of wherever the learner now is
      // would be its own bug, not a fix for one.
      if (mountedRef.current) {
        void navigate('/learn/paused', { replace: true });
      }
    } catch {
      if (mountedRef.current) {
        setPausing(false);
        setPauseFailed(true);
      }
    }
  }

  async function handleEndEarly() {
    setEndingEarly(true);
    setEndEarlyFailed(false);
    try {
      await client.sendAction(
        'end_early',
        { reasonCode: 'learner_requested' },
        crypto.randomUUID(),
      );
      // Only navigate on confirmed success — never claim the session has
      // ended via a `finally` or before the server has actually confirmed
      // it, same discipline as every other action here. Skip navigation if
      // the learner has already left via another route (see mountedRef
      // above) — the session really did end server-side, but this
      // component no longer owns what the learner should see next.
      if (mountedRef.current) {
        void navigate('/learn/ended', { replace: true });
      }
    } catch {
      // On failure, close the dialog rather than leaving it open — the
      // learner returns to PracticeTask with a retry note and the "I want
      // to stop" affordance still available to reopen the dialog and try
      // again. (Design decision, documented per the plan: keeping the
      // dialog open on failure was the alternative; closing it keeps the
      // failure/retry affordance in one consistent place with every other
      // action in this component, rather than introducing an inline error
      // state inside the dialog that nothing else here uses.)
      if (mountedRef.current) {
        setEndingEarly(false);
        setEndEarlyFailed(true);
        setStopDialogOpen(false);
      }
    }
  }

  async function handleAcknowledge() {
    setSubmitting(true);
    setActionError(false);
    try {
      const result = await client.sendAction('acknowledge', {}, idempotencyKey);
      const terminalRoute = TERMINAL_ROUTES[result.sessionStatus];
      if (terminalRoute !== undefined) {
        if (mountedRef.current) void navigate(terminalRoute, { replace: true });
        return;
      }
      if (result.practice) {
        if (mountedRef.current) showNext(result.practice);
      } else await load();
    } catch {
      setActionError(true);
    } finally {
      setSubmitting(false);
    }
  }

  if (loadError) {
    return (
      <PageFrame title="Your activity" audience="learner">
        <div className="mt-4">
          <LoadError
            message="Your activity could not be loaded."
            onRetry={() => {
              setLoadError(false);
              void load();
            }}
          />
        </div>
        <SafeExitLink />
      </PageFrame>
    );
  }

  if (practice === null) {
    return (
      <PageFrame title="Your activity" audience="learner">
        <p role="status" className="mt-4 text-muted">
          Loading your activity…
        </p>
      </PageFrame>
    );
  }

  if (practice.contentEligibilityException === 'CONTENT_INELIGIBLE') {
    return <ContentUnavailableState />;
  }

  if (practice.currentItem === null) {
    // The effect above navigates away by sessionStatus on the next tick;
    // render nothing distinctive in the meantime rather than a competing
    // terminal-state message.
    return (
      <PageFrame title="Your activity" audience="learner">
        <p role="status" className="mt-4 text-muted">
          Loading your activity…
        </p>
      </PageFrame>
    );
  }

  const prompt = readPrompt(practice.currentItem.content);
  if (prompt === null) {
    return (
      <PageFrame title="Your activity" audience="learner">
        <p
          role="alert"
          className="mt-4 rounded-xl border border-red-300 bg-red-50 p-4"
        >
          This task couldn't be displayed.
        </p>
        <SafeExitLink />
      </PageFrame>
    );
  }

  const requiresResponse = practice.currentItem.requiresResponse;
  const diagram = readPracticeDiagram(practice.currentItem.content);
  // Real content today only ever uses 'numeric' (a typed-number answer)
  // or 'none' (a facilitation note, requiresResponse=false, never reaches
  // this branch at all) -- 'text' below is a placeholder for a response
  // form this schema doesn't have real content for yet, kept as the
  // fallback so an unrecognised future form still gets a working input
  // rather than nothing.
  const isNumeric = practice.currentItem.responseForm === 'numeric';
  const choiceOptions =
    practice.currentItem.responseForm === 'selection'
      ? readOptions(practice.currentItem.content)
      : null;
  // Picture options need more room than a column of words. Fraction models
  // read well two across; a graph has a scale to read, so graphs stay one
  // across at full width.
  const pictureChoices =
    choiceOptions?.some((option) => option.diagram !== null) ?? false;
  const smallPictureChoices =
    pictureChoices &&
    (choiceOptions?.every(
      (option) => option.diagram?.type === 'fraction_model',
    ) ??
      false);
  const tickOptions =
    practice.currentItem.responseForm === 'tick_all'
      ? readListOptions(practice.currentItem.content, 'options')
      : null;
  const orderItems =
    practice.currentItem.responseForm === 'order'
      ? readListOptions(practice.currentItem.content, 'items')
      : null;
  const boxes =
    practice.currentItem.responseForm === 'boxes'
      ? readBoxes(practice.currentItem.content)
      : null;
  // Boxes typed into inside the sentence, or labelled boxes under it.
  const boxesInline = boxes !== null && boxesInSentence(prompt, boxes);
  const boxOrder = boxes ? boxesOrder(boxes, responseText) : null;
  const activeBox =
    boxes?.find((box) => box.id === activeBoxId) ?? boxes?.[0] ?? null;
  const boxProps =
    boxes && activeBox
      ? {
          boxes,
          value: responseText,
          activeId: activeBox.id,
          disabled: submitting,
          onChange: handleResponseChange,
          onActive: setActiveBoxId,
          onSubmit: () => {
            if (!submitting && boxOrder !== null) void handleSubmit('boxes');
          },
        }
      : null;
  // Sort into groups. Content that is not a proper sort question falls
  // through to the plain answer box, like any form this screen doesn't know.
  const sortQuestion =
    practice.currentItem.responseForm === 'sort'
      ? readSort(practice.currentItem.content)
      : null;
  const sortEntries = sortQuestion
    ? sortOrder(sortQuestion, responseText)
    : null;
  // Fraction entry: one fraction or mixed number, typed into three boxes.
  const isFraction = practice.currentItem.responseForm === 'fraction';
  const fractionEntries = isFraction
    ? fractionOrder(
        responseText,
        readFractionAccept(practice.currentItem.content),
      )
    : null;
  const activePart = isFractionPart(activeBoxId) ? activeBoxId : 'n';
  // Tap a point on the number line: the picture itself is the answer
  // control. The answer text is the chosen tick's value, as it will be sent.
  // Content without a proper number line falls through to the plain answer
  // box, like any form this screen doesn't know.
  // A "place the number" question (Year 2) is the same, with buttons to
  // move the dot as well (PlaceAnswer.tsx).
  const isPlace = practice.currentItem.responseForm === 'place';
  const pointLine =
    requiresResponse &&
    (practice.currentItem.responseForm === 'number_line_point' || isPlace)
      ? readNumberLineContent(practice.currentItem.content)
      : null;
  const pointAt = pointLine ? numberLineTick(pointLine, responseText) : null;
  // Tap points on the grid, and shade the parts: the picture is the answer
  // control here too. The answer text is one tapped point, or one shaded
  // part, on each line.
  const plotGrid =
    requiresResponse && practice.currentItem.responseForm === 'plot_point'
      ? readPlotGrid(practice.currentItem.content)
      : null;
  const plotPoints = plotGrid ? splitGridPoints(responseText) : [];
  const shadePicture =
    requiresResponse && practice.currentItem.responseForm === 'shade'
      ? readShadePicture(practice.currentItem.content)
      : null;
  const shadedParts = shadePicture
    ? splitShaded(responseText, shadePicture.parts)
    : [];
  const tapKind = plotGrid ? 'plot' : shadePicture ? 'shade' : null;
  // Draw the missing columns: the graph shows them as they are set with the
  // buttons under it. The answer text is one drawn column on each line.
  const buildQuestion =
    requiresResponse && practice.currentItem.responseForm === 'build_graph'
      ? readBuildGraph(practice.currentItem.content)
      : null;
  const buildEntries = buildQuestion
    ? buildOrder(responseText, buildQuestion)
    : null;
  // Set the clock: the clock and its buttons are the answer control. The
  // answer text is the time the hands show ("5:47"); empty is 12 o'clock.
  const setClock =
    requiresResponse && practice.currentItem.responseForm === 'set_clock';
  // Make the amount: coins and notes or blocks tapped into a tray. The
  // answer text is one piece in the tray on each line.
  const makeQuestion =
    requiresResponse && practice.currentItem.responseForm === 'build'
      ? readBuild(practice.currentItem.content)
      : null;
  const makeEntries = makeQuestion
    ? buildOrderFromTray(makeQuestion, responseText)
    : null;
  // Match: every item joined to its partner. The answer text is one joined
  // item on each line.
  const matchQuestion =
    requiresResponse && practice.currentItem.responseForm === 'match'
      ? readMatch(practice.currentItem.content)
      : null;
  const matchEntries = matchQuestion
    ? matchOrder(matchQuestion, responseText)
    : null;
  // Add pictures to rows of a picture graph (Year 1): the graph shows them
  // as they are added with the buttons under it. The answer text is one row
  // on each line.
  const rowsQuestion =
    requiresResponse &&
    practice.currentItem.responseForm === 'build_picture_graph'
      ? readBuildPictureGraph(practice.currentItem.content)
      : null;
  const rowsEntries = rowsQuestion
    ? fillOrder(responseText, rowsQuestion)
    : null;
  const tapEmpty = plotGrid
    ? plotPoints.length === 0
    : shadedParts.length === 0;
  const trimmedResponse = responseText.trim();
  // Typed as a number, but too big for the server (e.g. pasted "1e20").
  const numericTooBig =
    isNumeric &&
    trimmedResponse.length > 0 &&
    Number.isFinite(Number(trimmedResponse)) &&
    Math.abs(Number(trimmedResponse)) >= NUMERIC_LIMIT;
  const numericInvalid =
    isNumeric &&
    trimmedResponse.length > 0 &&
    (!Number.isFinite(Number(trimmedResponse)) || numericTooBig);

  function appendDigit(char: string) {
    if (char === '.' && responseText.includes('.')) return;
    if (char === '-' && responseText.length > 0) return;
    if (responseText.length >= NUMERIC_MAX_LENGTH) return;
    handleResponseChange(responseText + char);
  }

  function backspace() {
    handleResponseChange(responseText.slice(0, -1));
  }

  // The number pad on a several-boxes question types into the active box.
  function setActiveBoxText(change: (text: string) => string) {
    if (!boxes || !activeBox) return;
    const values = splitBoxValues(responseText);
    const text = values[activeBox.id] ?? '';
    const next = change(text);
    if (next === text) return;
    handleResponseChange(
      joinBoxValues(boxes, { ...values, [activeBox.id]: next }),
    );
  }

  // And on a fraction-entry question, into the box last tapped or tabbed to.
  function setActivePartText(change: (text: string) => string) {
    const parts = splitFractionParts(responseText);
    const next = change(parts[activePart]);
    if (next === parts[activePart]) return;
    handleResponseChange(joinFractionParts({ ...parts, [activePart]: next }));
  }

  return (
    <PageFrame title="Your activity" audience="learner">
      <p className="mt-4 text-muted">{practice.purpose}</p>
      {/* item_count sessions have a real fixed boundary (stopping_rule_value)
          to show as "of Y"; time_limit sessions' items_total is just how
          many session_item rows exist so far, not a true total, so showing
          it as "of Y" there would read as a real total when it isn't --
          just the current question number instead. */}
      <p className="mt-1 text-sm font-semibold text-muted">
        {practice.stoppingRuleType === 'item_count'
          ? `Question ${String(practice.itemsCompleted + 1)} of ${String(practice.itemsTotal)}`
          : `Question ${String(practice.itemsCompleted + 1)}`}
      </p>
      <TimeLeft
        limitSeconds={practice.timeLimitSeconds ?? null}
        usedSeconds={practice.secondsUsed ?? null}
      />
      {/* A quiet visual of the same "Question N of Y" line above -- only for
          item_count sessions, which have a real fixed total (a time_limit
          session has none, and a bar there would read as a countdown). It
          fills by questions already answered, never by time. aria-hidden:
          the text above already says it for screen readers. */}
      {practice.stoppingRuleType === 'item_count' &&
        practice.itemsTotal > 0 && (
          <div
            aria-hidden="true"
            className="mt-2 h-2 overflow-hidden rounded-full bg-paper-2"
          >
            <div
              className="h-full rounded-full bg-forest-600 transition-[width] duration-500"
              style={{
                width: `${String(
                  Math.min(
                    100,
                    Math.round(
                      (practice.itemsCompleted / practice.itemsTotal) * 100,
                    ),
                  ),
                )}%`,
              }}
            />
          </div>
        )}
      <p className="dm-question mt-6">
        {boxProps && boxesInline ? (
          <BoxesInSentence prompt={prompt} {...boxProps} />
        ) : isNumeric && prompt.includes('☐') ? (
          <>
            <MathText text={prompt.split('☐')[0] ?? ''} />
            <span
              aria-hidden="true"
              className="mx-1 inline-flex min-w-[2.5em] items-center justify-center rounded-md border-2 border-dashed border-forest-600 px-2 text-forest-800"
            >
              {responseText.trim() || '\u00a0'}
            </span>
            <MathText text={prompt.split('☐').slice(1).join('☐')} />
            <span className="sr-only">
              {' '}
              (The box is the missing number. Type it below.)
            </span>
          </>
        ) : (
          <MathText text={prompt} />
        )}
      </p>
      {/* Reads the question and its options aloud (Year 1 first, 24 Oct
          2026). Keyed by the question, so it stops when the child moves on.
          Not shown where the browser cannot speak. */}
      {canReadAloud() && (
        <div className="mt-3">
          <ReadAloudButton
            key={practice.currentItem.id}
            text={readAloudScript(practice.currentItem.content)}
          />
        </div>
      )}
      {pointLine ? (
        <div className="mt-4">
          {isPlace ? (
            <PlaceAnswer
              line={pointLine}
              chosen={pointAt}
              disabled={submitting}
              onChoose={(at) => {
                handleResponseChange(
                  at === null ? '' : canonicalNumberLineValue(pointLine, at),
                );
              }}
            />
          ) : (
            <NumberLineView
              line={pointLine}
              chosen={pointAt}
              disabled={submitting}
              onChoose={(at) => {
                handleResponseChange(
                  at === null ? '' : canonicalNumberLineValue(pointLine, at),
                );
              }}
            />
          )}
        </div>
      ) : plotGrid ? (
        <div className="mt-4 flex justify-center">
          <CoordinateGrid
            graph={plotGrid}
            chosen={plotPoints}
            disabled={submitting}
            onToggle={(x, y) => {
              handleResponseChange(
                joinGridPoints(toggleGridPoint(plotPoints, { x, y })),
              );
            }}
          />
        </div>
      ) : buildQuestion ? (
        <div className="mt-4 grid gap-4">
          {buildQuestion.table && (
            <PracticeDiagramView diagram={buildQuestion.table} />
          )}
          <PracticeDiagramView
            diagram={builtGraph(responseText, buildQuestion)}
          />
        </div>
      ) : rowsQuestion ? (
        <div className="mt-4">
          <PracticeDiagramView
            diagram={filledGraph(responseText, rowsQuestion)}
          />
        </div>
      ) : shadePicture ? (
        <div className="mt-4 flex justify-center">
          {shadePicture.picture.type === 'shade_grid' ? (
            <ShadeGridView
              grid={shadePicture.picture}
              shaded={shadedParts}
              disabled={submitting}
              onToggle={(index) => {
                handleResponseChange(
                  toggleShaded(shadedParts, index).join('\n'),
                );
              }}
            />
          ) : (
            <FractionModelView
              model={shadePicture.picture}
              picked={shadedParts}
              disabled={submitting}
              onToggle={(index) => {
                handleResponseChange(
                  toggleShaded(shadedParts, index).join('\n'),
                );
              }}
            />
          )}
        </div>
      ) : (
        // A set-the-clock question's clock is its answer control, below.
        diagram &&
        !setClock && (
          <div className="mt-4">
            <PracticeDiagramView diagram={diagram} />
          </div>
        )
      )}

      {/* Floats over the page instead of being inserted into it, so the
          question and answer boxes never jump down and back up when it
          appears and fades (owner report 2026-10-03: flicker after Submit). */}
      {justSubmitted && (
        <p
          role="status"
          className="pointer-events-none fixed inset-x-0 top-4 z-40 mx-auto w-fit max-w-[calc(100%-2rem)] rounded-xl border border-forest-600/15 bg-sage px-4 py-2 font-semibold text-forest-800 shadow-card"
        >
          Got it! On to the next one.
        </p>
      )}

      {actionError && (
        <div className="mt-6">
          <ErrorSummary errors={[GENERIC_ACTION_ERROR]} />
        </div>
      )}

      {requiresResponse && (tickOptions || orderItems) ? (
        <div className="mx-auto mt-6 grid w-full max-w-sm grid-cols-1 gap-2.5">
          {tickOptions ? (
            <TickAllAnswer
              options={tickOptions}
              value={responseText}
              disabled={submitting}
              onChange={handleResponseChange}
            />
          ) : orderItems ? (
            <OrderAnswer
              items={orderItems}
              value={responseText}
              disabled={submitting}
              onChange={handleResponseChange}
            />
          ) : null}
          <div className="sticky bottom-0 z-10 -mx-2 mt-1 bg-white/95 px-2 py-3 backdrop-blur">
            <button
              type="button"
              disabled={
                submitting ||
                splitAnswer(responseText).length === 0 ||
                (orderItems !== null &&
                  splitAnswer(responseText).length !== orderItems.length)
              }
              className="dm-btn-primary w-full"
              onClick={() => {
                void handleSubmit('list');
              }}
            >
              Submit
            </button>
          </div>
        </div>
      ) : requiresResponse && boxProps ? (
        <div className="mx-auto mt-6 grid w-full max-w-sm grid-cols-1 gap-2.5">
          {!boxesInline && <LabelledBoxes {...boxProps} />}
          <NumberPad
            disabled={submitting}
            deleteDisabled={
              (splitBoxValues(responseText)[boxProps.activeId] ?? '').length ===
              0
            }
            onKey={(key) => {
              setActiveBoxText((text) => pressBoxKey(text, key));
            }}
            onDelete={() => {
              setActiveBoxText((text) => text.slice(0, -1));
            }}
          />
          <div className="sticky bottom-0 z-10 -mx-2 mt-1 bg-white/95 px-2 py-3 backdrop-blur">
            <button
              type="button"
              disabled={submitting || boxOrder === null}
              className="dm-btn-primary w-full"
              onClick={() => {
                void handleSubmit('boxes');
              }}
            >
              Submit
            </button>
          </div>
        </div>
      ) : requiresResponse && isFraction ? (
        <div className="mx-auto mt-6 grid w-full max-w-sm grid-cols-1 gap-2.5">
          <FractionAnswer
            value={responseText}
            negatives={readFractionNegatives(practice.currentItem.content)}
            activeId={activePart}
            disabled={submitting}
            onChange={handleResponseChange}
            onActive={setActiveBoxId}
            onSubmit={() => {
              if (!submitting && fractionEntries !== null)
                void handleSubmit('fraction');
            }}
          />
          <NumberPad
            digitsOnly
            disabled={submitting}
            deleteDisabled={
              splitFractionParts(responseText)[activePart].length === 0
            }
            onKey={(key) => {
              setActivePartText((text) => pressFractionKey(text, key));
            }}
            onDelete={() => {
              setActivePartText((text) => text.slice(0, -1));
            }}
          />
          <div className="sticky bottom-0 z-10 -mx-2 mt-1 bg-white/95 px-2 py-3 backdrop-blur">
            <button
              type="button"
              disabled={submitting || fractionEntries === null}
              className="dm-btn-primary w-full"
              onClick={() => {
                void handleSubmit('fraction');
              }}
            >
              Submit
            </button>
          </div>
        </div>
      ) : pointLine ? (
        // No number pad: the line above is tapped.
        <div className="mx-auto mt-6 grid w-full max-w-sm grid-cols-1 gap-2.5">
          <div className="sticky bottom-0 z-10 -mx-2 mt-1 bg-white/95 px-2 py-3 backdrop-blur">
            <button
              type="button"
              disabled={submitting || pointAt === null}
              className="dm-btn-primary w-full"
              onClick={() => {
                void handleSubmit('point');
              }}
            >
              Submit
            </button>
          </div>
        </div>
      ) : buildQuestion ? (
        <div className="mx-auto mt-4 grid w-full max-w-md grid-cols-1 gap-2.5">
          <BuildGraphAnswer
            question={buildQuestion}
            value={responseText}
            disabled={submitting}
            onChange={handleResponseChange}
          />
          <div className="sticky bottom-0 z-10 -mx-2 mt-1 bg-white/95 px-2 py-3 backdrop-blur">
            <button
              type="button"
              disabled={submitting || buildEntries === null}
              className="dm-btn-primary w-full"
              onClick={() => {
                void handleSubmit('build');
              }}
            >
              Submit
            </button>
          </div>
        </div>
      ) : rowsQuestion ? (
        <div className="mx-auto mt-4 grid w-full max-w-md grid-cols-1 gap-2.5">
          <BuildPictureGraphAnswer
            question={rowsQuestion}
            value={responseText}
            disabled={submitting}
            onChange={handleResponseChange}
          />
          <div className="sticky bottom-0 z-10 -mx-2 mt-1 bg-white/95 px-2 py-3 backdrop-blur">
            <button
              type="button"
              disabled={submitting || rowsEntries === null}
              className="dm-btn-primary w-full"
              onClick={() => {
                void handleSubmit('rows');
              }}
            >
              Submit
            </button>
          </div>
        </div>
      ) : tapKind ? (
        // No number pad: the picture above is tapped.
        <div className="mx-auto mt-6 grid w-full max-w-sm grid-cols-1 gap-2.5">
          <div className="sticky bottom-0 z-10 -mx-2 mt-1 grid gap-2 bg-white/95 px-2 py-3 backdrop-blur">
            {shadePicture && (
              <p role="status" className="text-center font-semibold text-muted">
                {String(shadedParts.length)} of {String(shadePicture.parts)}{' '}
                shaded
              </p>
            )}
            <button
              type="button"
              disabled={submitting || tapEmpty}
              className="dm-btn-primary w-full"
              onClick={() => {
                void handleSubmit(tapKind);
              }}
            >
              Submit
            </button>
          </div>
        </div>
      ) : setClock ? (
        <div className="mx-auto mt-4 grid w-full max-w-md grid-cols-1 gap-2.5">
          <SetClockAnswer
            value={responseText}
            disabled={submitting}
            onChange={handleResponseChange}
          />
          <div className="sticky bottom-0 z-10 -mx-2 mt-1 bg-white/95 px-2 py-3 backdrop-blur">
            <button
              type="button"
              disabled={submitting}
              className="dm-btn-primary w-full"
              onClick={() => {
                void handleSubmit('clock');
              }}
            >
              Submit
            </button>
          </div>
        </div>
      ) : makeQuestion ? (
        <div className="mx-auto mt-6 grid w-full max-w-md grid-cols-1 gap-2.5">
          <MakeAmountAnswer
            question={makeQuestion}
            value={responseText}
            disabled={submitting}
            onChange={handleResponseChange}
          />
          <div className="sticky bottom-0 z-10 -mx-2 mt-1 bg-white/95 px-2 py-3 backdrop-blur">
            <button
              type="button"
              disabled={submitting || makeEntries === null}
              className="dm-btn-primary w-full"
              onClick={() => {
                void handleSubmit('make');
              }}
            >
              Submit
            </button>
          </div>
        </div>
      ) : matchQuestion ? (
        <div className="mx-auto mt-6 grid w-full max-w-2xl grid-cols-1 gap-2.5">
          <MatchAnswer
            question={matchQuestion}
            value={responseText}
            disabled={submitting}
            onChange={handleResponseChange}
          />
          <div className="sticky bottom-0 z-10 -mx-2 mt-1 grid gap-2 bg-white/95 px-2 py-3 backdrop-blur">
            <p role="status" className="text-center font-semibold text-muted">
              {String(joinedCount(matchQuestion, responseText))} of{' '}
              {String(matchQuestion.left.length)} joined
            </p>
            <button
              type="button"
              disabled={submitting || matchEntries === null}
              className="dm-btn-primary w-full"
              onClick={() => {
                void handleSubmit('match');
              }}
            >
              Submit
            </button>
          </div>
        </div>
      ) : requiresResponse && sortQuestion ? (
        <div className="mx-auto mt-6 grid w-full max-w-2xl grid-cols-1 gap-2.5">
          <SortAnswer
            question={sortQuestion}
            value={responseText}
            disabled={submitting}
            onChange={handleResponseChange}
          />
          <div className="sticky bottom-0 z-10 -mx-2 mt-1 grid gap-2 bg-white/95 px-2 py-3 backdrop-blur">
            <p role="status" className="text-center font-semibold text-muted">
              {String(sortedCount(sortQuestion, responseText))} of{' '}
              {String(sortQuestion.items.length)} sorted
            </p>
            <button
              type="button"
              disabled={submitting || sortEntries === null}
              className="dm-btn-primary w-full"
              onClick={() => {
                void handleSubmit('sort');
              }}
            >
              Submit
            </button>
          </div>
        </div>
      ) : requiresResponse && choiceOptions ? (
        <fieldset
          className={`mx-auto mt-6 grid w-full grid-cols-1 gap-2.5 ${
            smallPictureChoices
              ? 'max-w-2xl sm:grid-cols-2'
              : pictureChoices
                ? 'max-w-md'
                : 'max-w-sm'
          }`}
        >
          <legend className="mb-2.5 font-semibold">Choose your answer</legend>
          {choiceOptions.map((option, index) => {
            const chosen = responseText === option.id;
            return (
              <label
                key={option.id}
                className={`flex min-h-14 cursor-pointer items-center gap-3 rounded-xl border-2 px-4 text-xl font-semibold transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-forest-600 ${
                  option.diagram ? 'py-3' : ''
                } ${
                  chosen
                    ? 'border-forest-600 bg-sage text-forest-800'
                    : 'border-line bg-white text-ink hover:border-line-strong'
                }`}
              >
                <input
                  type="radio"
                  name="practice-choice"
                  value={option.id}
                  checked={chosen}
                  disabled={submitting}
                  className="size-5 accent-forest-600"
                  onChange={() => {
                    handleResponseChange(option.id);
                  }}
                  onKeyDown={(event) => {
                    // Enter on the chosen answer submits it.
                    if (event.key === 'Enter' && !submitting && chosen) {
                      event.preventDefault();
                      void handleSubmit('selection');
                    }
                  }}
                />
                {option.diagram ? (
                  // A picture option: the picture, with its label under it
                  // when it has one. One with no label is named by its
                  // place, then by the picture's own description.
                  <span className="flex min-w-0 flex-1 flex-col gap-2">
                    {option.label === '' && (
                      <span className="sr-only">
                        Option {String.fromCharCode(65 + index)}.
                      </span>
                    )}
                    <PracticeDiagramView diagram={option.diagram} />
                    {option.label !== '' && <MathText text={option.label} />}
                  </span>
                ) : (
                  <MathText text={option.label} />
                )}
              </label>
            );
          })}
          <div
            className={`sticky bottom-0 z-10 -mx-2 mt-1 bg-white/95 px-2 py-3 backdrop-blur ${
              smallPictureChoices ? 'sm:col-span-2' : ''
            }`}
          >
            <button
              type="button"
              disabled={submitting || responseText.length === 0}
              className="dm-btn-primary w-full"
              onClick={() => {
                void handleSubmit('selection');
              }}
            >
              Submit
            </button>
          </div>
        </fieldset>
      ) : requiresResponse && isNumeric ? (
        <div className="mx-auto mt-6 grid w-full max-w-sm grid-cols-1 gap-2.5">
          <label htmlFor="practice-response" className="font-semibold">
            Your answer
          </label>
          {/* A real, focusable, keyboard/screen-reader-operable input stays
              the actual source of truth -- inputMode="numeric" brings up a
              number keyboard on mobile without losing keyboard support or
              paste. The keypad below is a touch-friendly enhancement on
              top of the same state, never the only way to answer. */}
          <input
            id="practice-response"
            type="text"
            inputMode="numeric"
            className="dm-field w-full min-w-0 text-center text-3xl font-semibold tracking-wide"
            value={responseText}
            maxLength={NUMERIC_MAX_LENGTH}
            disabled={submitting}
            aria-invalid={numericInvalid}
            onChange={(event) => {
              handleResponseChange(event.target.value);
            }}
            onKeyDown={(event) => {
              if (
                event.key === 'Enter' &&
                !submitting &&
                trimmedResponse.length > 0 &&
                !numericInvalid
              ) {
                event.preventDefault();
                void handleSubmit('numeric');
              }
            }}
          />
          {numericInvalid && (
            <p role="alert" className="text-sm text-red-700">
              {numericTooBig ? 'That number is too big.' : 'Enter a number.'}
            </p>
          )}
          <NumberPad
            disabled={submitting}
            deleteDisabled={responseText.length === 0}
            onKey={appendDigit}
            onDelete={backspace}
          />
          <div className="sticky bottom-0 z-10 -mx-2 mt-1 bg-white/95 px-2 py-3 backdrop-blur">
            <button
              type="button"
              disabled={
                submitting || trimmedResponse.length === 0 || numericInvalid
              }
              className="dm-btn-primary w-full"
              onClick={() => {
                void handleSubmit('numeric');
              }}
            >
              Submit
            </button>
          </div>
        </div>
      ) : requiresResponse ? (
        <div className="mt-6 grid grid-cols-1 gap-2.5">
          <label htmlFor="practice-response" className="font-semibold">
            Your answer
          </label>
          <textarea
            id="practice-response"
            className="dm-field min-h-28 resize-none text-lg"
            value={responseText}
            maxLength={500}
            disabled={submitting}
            onChange={(event) => {
              handleResponseChange(event.target.value);
            }}
          />
          <div>
            <button
              type="button"
              disabled={submitting || responseText.trim().length === 0}
              className="dm-btn-primary"
              onClick={() => {
                void handleSubmit('text');
              }}
            >
              Submit
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-6">
          <button
            type="button"
            disabled={submitting}
            className="dm-btn-primary"
            onClick={() => {
              void handleAcknowledge();
            }}
          >
            Continue
          </button>
        </div>
      )}

      {/* Why Submit is greyed out (design audit 2026-10-04). */}
      {requiresResponse &&
        !submitting &&
        !setClock &&
        (boxProps
          ? boxOrder === null
          : sortQuestion
            ? sortEntries === null
            : isFraction
              ? fractionEntries === null
              : pointLine
                ? pointAt === null
                : tapKind
                  ? tapEmpty
                  : buildQuestion
                    ? buildEntries === null
                    : makeQuestion
                      ? makeEntries === null
                      : matchQuestion
                        ? matchEntries === null
                        : rowsQuestion
                          ? rowsEntries === null
                          : trimmedResponse.length === 0) && (
          <p className="mt-3 text-center text-base text-muted">
            {boxProps
              ? 'Type a number in each box, then press Submit.'
              : sortQuestion
                ? 'Put every one in a group, then press Submit.'
                : isFraction
                  ? 'Type the numerator and denominator, then press Submit.'
                  : pointLine && isPlace
                    ? 'Tap where the number goes on the line, then press Submit.'
                    : pointLine
                      ? 'Tap a point on the line, then press Submit.'
                      : plotGrid
                        ? 'Tap a point on the grid, then press Submit.'
                        : shadePicture
                          ? 'Shade the parts, then press Submit.'
                          : buildQuestion
                            ? 'Draw each missing column, then press Submit.'
                            : makeQuestion?.kind === 'money'
                              ? 'Tap coins and notes to make the amount, then press Submit.'
                              : makeQuestion
                                ? 'Tap blocks to make the number, then press Submit.'
                                : matchQuestion
                                  ? 'Join each one to its partner, then press Submit.'
                                  : rowsQuestion
                                    ? 'Add pictures to each row, then press Submit.'
                                    : 'Answer the question, then press Submit.'}
          </p>
        )}

      <div className="mt-8 flex flex-wrap gap-3 border-t border-line pt-6">
        <button
          type="button"
          className="dm-btn-secondary"
          onClick={() => {
            void navigate('/learn/help');
          }}
        >
          Help
        </button>
        <button
          type="button"
          disabled={pausing}
          className="dm-btn-secondary"
          onClick={() => {
            void handlePause();
          }}
        >
          Pause
        </button>
      </div>

      {pauseFailed && (
        <p role="alert" className="mt-4 text-sm text-muted">
          {PAUSE_NOT_SAVED_NOTE}
        </p>
      )}

      {/* Tertiary, visually de-emphasized relative to Submit/Continue and
          Help/Pause — ending a session is a genuinely hard-to-reverse
          action, so it gets its own smaller, quieter affordance rather
          than sitting alongside the primary controls. "Report a problem"
          is pure navigation to a dedicated screen (unlike end_early's
          in-place confirm dialog) since problem-reporting needs a
          category choice, not a single confirm — no sendAction call is
          made from this affordance itself. */}
      <details className="mt-6 border-t border-line pt-4">
        <summary className="dm-link-quiet inline-flex min-h-11 cursor-pointer items-center">
          More options
        </summary>
        <div className="mt-2 flex flex-wrap gap-x-6">
          <button
            type="button"
            disabled={endingEarly}
            className="dm-link-quiet"
            onClick={() => {
              setStopDialogOpen(true);
            }}
          >
            I want to stop
          </button>
          <button
            type="button"
            className="dm-link-quiet"
            onClick={() => {
              void navigate('/learn/problem');
            }}
          >
            Report a problem
          </button>
          <SafeExitLink quiet />
        </div>
      </details>

      {endEarlyFailed && (
        <p role="alert" className="mt-4 text-sm text-muted">
          {END_EARLY_NOT_SAVED_NOTE}
        </p>
      )}

      {stopDialogOpen && (
        <Dialog
          title="Are you sure?"
          onClose={() => {
            setStopDialogOpen(false);
          }}
        >
          <p>{END_EARLY_CONFIRM_TEXT}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={endingEarly}
              className="inline-flex min-h-12 items-center rounded-xl bg-red-800 px-5 font-semibold text-white transition-colors hover:bg-red-900 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() => {
                void handleEndEarly();
              }}
            >
              Yes, stop
            </button>
            <button
              type="button"
              disabled={endingEarly}
              className="dm-btn-secondary"
              onClick={() => {
                setStopDialogOpen(false);
              }}
            >
              Cancel
            </button>
          </div>
        </Dialog>
      )}
    </PageFrame>
  );
}

// A calm "about N minutes left" line when the teacher set a timer (test run
// 2026-10-02). Minutes, not a ticking clock -- no pressure, just fair
// warning. Counts on from the time used when the question loaded.
function TimeLeft({
  limitSeconds,
  usedSeconds,
}: {
  limitSeconds: number | null;
  usedSeconds: number | null;
}) {
  const [loadedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (limitSeconds === null) return;
    const id = window.setInterval(() => {
      setNow(Date.now());
    }, 15000);
    return () => {
      window.clearInterval(id);
    };
  }, [limitSeconds]);
  if (limitSeconds === null || usedSeconds === null) return null;
  const left = limitSeconds - usedSeconds - Math.floor((now - loadedAt) / 1000);
  const minutes = Math.ceil(left / 60);
  return (
    <p className="mt-1 text-sm text-muted">
      {left <= 60
        ? 'Less than a minute left'
        : `About ${String(minutes)} minutes left`}
    </p>
  );
}
